import React from 'react';
import { SectionLayout } from '@/components/shared/section-layout';
import { TabsContent } from '@/components/ui/tabs';
import { InvoicesDashboard } from './components/InvoicesDashboard';
import { InvoicesTable } from './components/InvoicesTable';
import { PaymentsTable } from './components/PaymentsTable';
import { GenericTabBar } from '@/styles/GenericTabBar';
import { useVisibleTabs } from '@/hooks/useVisibleTabs';
import { NothingAvailable } from '@/components/NavigationUnavailable';
import type { NavKey } from '@/lib/navigation-catalog';
import '@/design-system/typography.css';
import '@/design-system/forms.css';

type TabKey = 'overview' | 'invoices' | 'payments';

// which tabs a role gets is the navigation matrix (nav_visibility), one element per tab
const TAB_KEYS: Record<TabKey, NavKey> = { overview: 'cost_management.invoices.tab.overview', invoices: 'cost_management.invoices.tab.invoices', payments: 'cost_management.invoices.tab.payments' };
const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'payments', label: 'Payments' },
];

export function InvoicesPage() {
  const visible = useVisibleTabs(TABS, TAB_KEYS);
  const [requestedTab, setActiveTab] = React.useState<TabKey>('overview');
  const activeTab = visible.activeOf(requestedTab) as TabKey | undefined;

  const getTabTitle = (tab: TabKey): string => {
    switch (tab) {
      case 'overview':
        return 'Dashboard';
      case 'invoices':
        return 'Invoice Management';
      case 'payments':
        return 'Invoice Payment Tracking';
      default:
        return '';
    }
  };

  if (activeTab === undefined) return <SectionLayout title="Invoices"><NothingAvailable message="No section of Invoices is available for your account." /></SectionLayout>;

  return (
    <SectionLayout title="Invoices">
      <div className="mb-6">
        <h2 className="font-section-header mb-4">{getTabTitle(activeTab)}</h2>
      </div>
      <GenericTabBar
        tabs={visible.tabs}
        activeTab={activeTab}
        onTabChange={(value) => setActiveTab(value as TabKey)}
      >
        {visible.has('overview') && (
          <TabsContent value="overview" className="mt-6">
            <InvoicesDashboard />
          </TabsContent>
        )}
        {visible.has('invoices') && (
          <TabsContent value="invoices" className="mt-6">
            <InvoicesTable />
          </TabsContent>
        )}
        {visible.has('payments') && (
          <TabsContent value="payments" className="mt-6">
            <PaymentsTable />
          </TabsContent>
        )}
      </GenericTabBar>
    </SectionLayout>
  );
}